import React, { useEffect, useState } from 'react';
import { ReactComponent as TopRiderIcon } from 'assets/icons/medal-star.svg';
import { ReactComponent as RightArrowIcon } from 'assets/icons/arrow-circle-right.svg';
import {
  DateRangeSelector,
  ENUM_DATE_RANGE,
  BaseModal,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import { RiderType } from 'models/rider.model';
import { apiService } from 'utils/api';
import DisplayNameIcon from 'components/base/display-name-icon';
import { formatNumber, getUserName } from 'utils/functions';
import RiderDetailsSideModal from 'pages/logistics/partners/partner-modals/rider-details-modal';
import { STORAGE_KEYS } from 'utils/constants';

type TopRiderResult = RiderType & {
  avg_delivery_fee: number;
  avg_duration: number;
  count: string;
  delivery_fee: number;
  duration: number | null;
  rider_id: string;
};
const TopRidersModal = ({ show, onClose }: { show: boolean; onClose: () => void }) => {
  const [loading, setLoading] = useState(true);
  const [allRiders, setAllRiders] = useState<TopRiderResult[]>([]);
  const [activeRider, setActiveRider] = useState<TopRiderResult>();
  const [showRiderDetails, setShowRiderDetails] = useState(false);
  const [dateRange, setDateRange] = useState(getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK));
  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN);

  const loadData = async () => {
    setLoading(true);
    const { startDate: start, endDate: end } = dateRange;
    try {
      const res = await apiService.get('logistics/report/top-riders', {
        params: {
          start,
          end,
          cokitchen_id: cokitchenId
        },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      setAllRiders(res?.data?.results);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      setDateRange(getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK));
    }
  }, [show]);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const viewDetails = () => {
    setShowRiderDetails(!showRiderDetails);
  };

  return (
    <div>
      <BaseModal
        position='right'
        isOpen={show}
        onRequestClose={onClose}
        title='Top Riders'
        titleIcon={<TopRiderIcon />}
        size='wide'>
        <div className='w-full space-y-5 my-3'>
          <div className='flex flex-wrap gap-4 justify-between items-center'>
            <span>Showing top riders</span>
            <DateRangeSelector showTime onChange={({ date }) => setDateRange(date)} />
          </div>
          {loading ? (
            <div className='text-center mt-20 leading-[30vh]'>
              <h2>Loading...</h2>
            </div>
          ) : (
            allRiders?.map((item, i) => (
              <div
                key={i}
                className='w-full flex max-lg:flex-wrap items-center gap-2 justify-between text-gray-8 text-xs rounded-[4px] border border-[#E3E6E8] p-3 cursor-pointer'
                onClick={() => {
                  setActiveRider(item);
                  viewDetails();
                }}>
                <span className='inline-flex items-center gap-2 basis-full md:basis-[65%] lg:basis-[40%] text-sm text-gray-6 font-medium px-2'>
                  <DisplayNameIcon user={item} />
                  <span className='whitespace-nowrap'>{getUserName(item)}</span>
                </span>
                <span className='md:basis-[30%] lg:basis-[20%] px-2 whitespace-nowrap text-right'>
                  {item?.count} total trips
                </span>
                <span className='basis-[15%] px-2 whitespace-nowrap'>
                  {item?.avg_duration.toFixed(0)} mins
                </span>
                <span className='basis-[20%] px-2 whitespace-nowrap'>
                  {formatNumber(item.delivery_fee)} earned
                </span>
                <span className='basis-[5%]'>
                  <RightArrowIcon />
                </span>
              </div>
            ))
          )}
        </div>
      </BaseModal>

      <RiderDetailsSideModal
        rider={activeRider ?? ({} as RiderType)}
        show={showRiderDetails}
        onClose={viewDetails}
      />
    </div>
  );
};

export default TopRidersModal;
